import type { AppraisalAnnexureReviewData } from "../EmployeeAppraisal/components/appraisal-annexure-review.tsx";
import type { AppraisalLetterPreviewData } from "../EmployeeAppraisal/appraisal-letter-preview.ts";
import { annualAmountToRoundedMonthly, annualBenefitsFromBasic, esicEmployeeFromBasic, esicEmployerFromBasic, isEsicEligible, providentFundFromBasic } from "../EmployeeAppraisal/appraisal-annexure-utils.ts";
import type { AppraisalLetterType } from "../EmployeeAppraisal/types.ts";
import type { AppraisalDetail } from "./types.ts";

function dateValue(value?: string): Date | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toAppraisalAnnexureReviewData(detail: AppraisalDetail): AppraisalAnnexureReviewData {
  const basic = Math.round(detail.compensation?.basic ?? 0);
  const automaticPf = providentFundFromBasic(basic);
  const grossWithoutEsic = basic
    + Math.round(detail.compensation?.pfEmployeePart ?? automaticPf)
    + Math.round(detail.allowances?.specialAllowance ?? 0)
    + Math.round(detail.allowances?.houseRentAllowance ?? 0)
    + Math.round(detail.allowances?.internetReimbursement ?? 0);
  const monthlyGross = detail.baseValue ?? grossWithoutEsic;
  const esicEligible = isEsicEligible(monthlyGross);
  const components = {
    basic,
    pfEmployeePart: Math.round(detail.compensation?.pfEmployeePart ?? automaticPf),
    esicEmployeePart: esicEligible
      ? Math.round(detail.compensation?.esic ?? esicEmployeeFromBasic(basic))
      : 0,
    specialAllowance: Math.round(detail.allowances?.specialAllowance ?? 0),
    houseRentAllowance: Math.round(detail.allowances?.houseRentAllowance ?? 0),
    internetReimbursement: Math.round(detail.allowances?.internetReimbursement ?? 0),
    gratuity: Math.round(detail.statutory?.gratuity ?? 0),
    pfEmployerPart: Math.round(detail.statutory?.pfEmployerPart ?? automaticPf),
    esicEmployerPart: esicEligible
      ? Math.round(detail.statutory?.esic ?? esicEmployerFromBasic(basic))
      : 0,
    annualFixedComponent: detail.annualComponent?.enabled ? Math.round(detail.annualComponent.amount ?? 0) : 0,
  };
  const monthlyBenefits = components.gratuity + components.pfEmployerPart + components.esicEmployerPart;
  return {
    components,
    monthlyGross,
    annualGross: monthlyGross * 12,
    monthlyBenefits,
    annualBenefits: annualBenefitsFromBasic(components.basic, components.pfEmployerPart, components.esicEmployerPart),
    monthlyCtc: Math.round(monthlyGross + monthlyBenefits + annualAmountToRoundedMonthly(components.annualFixedComponent)),
    annualCtc: monthlyGross * 12 + annualBenefitsFromBasic(components.basic, components.pfEmployerPart, components.esicEmployerPart) + components.annualFixedComponent,
    esicEligible,
    annualComponentEnabled: detail.annualComponent?.enabled,
    remarks: detail.annualComponent?.remarks,
  };
}

export function toAppraisalLetterPreviewData(detail: AppraisalDetail): AppraisalLetterPreviewData {
  const review = toAppraisalAnnexureReviewData(detail);
  const components = review.components;
  const letterType = (["APPRAISAL", "PROMOTION", "APPRAISAL_WITH_PROMOTION"].includes(detail.letterType)
    ? detail.letterType
    : "APPRAISAL") as AppraisalLetterType;

  return {
    employeeName: detail.employeeName,
    employeeType: detail.employeeType,
    salutation: detail.salutation || "",
    personalDetail: {
      empName: detail.employeeName,
      firstName: detail.employeeName.split(" ")[0],
      designation: detail.designation,
      currentLocation: detail.workLocation ?? detail.currentLocation ?? detail.location,
    },
    basicValues: {
      employeeId: String(detail.employeeId),
      letterType,
      letterIssueDate: dateValue(detail.letterIssueDate),
      effectiveDate: dateValue(detail.effectiveDate),
      noticePeriod: detail.noticePeriod || "-",
      designationId: "",
      designation: detail.designationChangeText || detail.designation || "",
      annualComponent: detail.annualComponent?.enabled ? "YES" : "NO",
      annualComponentRemarks: detail.annualComponent?.remarks || "",
    },
    compensationSummary: {
      ...review,
      annualComponent: components.annualFixedComponent,
      takeHome: Math.max(0, review.monthlyGross - components.pfEmployeePart - components.esicEmployeePart),
    },
    annexureValues: {
      annualCtc: String(review.annualCtc),
      monthlyGross: String(review.monthlyGross),
      basic: String(components.basic),
      pfEmployeePart: String(components.pfEmployeePart),
      esicEmployeePart: String(components.esicEmployeePart),
      specialAllowance: String(components.specialAllowance),
      houseRentAllowance: String(components.houseRentAllowance),
      conveyanceAllowance: String(detail.allowances?.conveyanceAllowance ?? 0),
      helperAndDriver: String(detail.allowances?.helperAndDriver ?? 0),
      lta: String(detail.allowances?.lta ?? 0),
      mealReimbursement: String(detail.allowances?.mealReimbursement ?? 0),
      internetReimbursement: String(components.internetReimbursement),
      gymHealthClubReimbursement: String(detail.allowances?.gymHealthClubReimbursement ?? 0),
      gratuity: String(components.gratuity),
      pfEmployerPart: String(components.pfEmployerPart),
      esicEmployerPart: String(components.esicEmployerPart),
      annualComponentEnabled: Boolean(detail.annualComponent?.enabled),
      annualComponentAmount: String(components.annualFixedComponent),
      annualComponentRemarks: detail.annualComponent?.remarks || "",
      remarks: detail.remarks || "",
    },
  };
}
